<template>
  <div class="strategyPlay">
    <p v-if="error" class="spError">{{ error }}</p>

    <template v-if="details">
      <section class="spCard">
        <p v-if="details.description" class="spWhy">{{ details.description }}</p>
        <StrategyBriefInput v-model="brief" :disabled="saving" />
        <div class="spActions">
          <p v-if="running" class="spMuted spActionsNote" data-test="run-in-progress">A run is in progress. Changes apply to the next run.</p>
          <button class="btn secondary" type="button" :disabled="saving || !canRun || running" @click="runNow">Run now</button>
          <button class="btn secondary" type="button" :disabled="saving || !canSave" @click="save(brief.trim())">Save</button>
          <button class="btn primary" type="button" :disabled="saving || !canSave || running" @click="saveAndRun(brief.trim())">
            Save and run
          </button>
        </div>
      </section>

      <section v-if="latestRun && !running" class="spCard">
        <div class="spCardHead">
          <h3 class="spTitle">Last run</h3>
          <span class="spStatus" :class="`spStatus--${latestRun.status}`">{{ statusLabel(latestRun.status) }}</span>
        </div>
        <p v-if="latestRun.status_summary" class="spSummary">{{ latestRun.status_summary }}</p>
        <dl v-if="latestRun.account" class="spAttest" data-test="attestation">
          <template v-if="latestRun.account.why">
            <dt>Why</dt>
            <dd>{{ latestRun.account.why }}</dd>
          </template>
          <template v-if="latestRun.account.evidence?.length">
            <dt>Sources</dt>
            <dd><ul><li v-for="e in latestRun.account.evidence" :key="e">{{ e }}</li></ul></dd>
          </template>
          <template v-if="latestRun.account.uncertain">
            <dt>Not sure about</dt>
            <dd>{{ latestRun.account.uncertain }}</dd>
          </template>
        </dl>
      </section>

      <section class="spCounts">
        <router-link class="spCount" :to="`/sales-plays/${playId}/candidates/people?stage=qualified`">
          <span class="spCountNum">{{ details.counts.kept }}</span>
          <span class="spCountLabel">Prospects</span>
        </router-link>
        <router-link class="spCount" :to="`/sales-plays/${playId}/candidates/people?stage=disqualified`">
          <span class="spCountNum">{{ details.counts.excluded }}</span>
          <span class="spCountLabel">Excluded</span>
        </router-link>
        <router-link class="spCount" to="/actions">
          <span class="spCountNum">{{ details.counts.drafts }}</span>
          <span class="spCountLabel">Drafts</span>
        </router-link>
      </section>

      <section class="spCard">
        <h3 class="spTitle">What worked so far</h3>
        <p v-if="details.notes" class="spNotes">{{ details.notes }}</p>
        <p v-else class="spMuted">No notes yet.</p>
      </section>

      <section class="spCard">
        <h3 class="spTitle">Runs</h3>
        <ul v-if="details.runs.length" class="spRuns">
          <li v-for="run in details.runs" :key="run.id" class="spRun">
            <div class="spRunHead">
              <span class="spStatus" :class="`spStatus--${run.status}`">{{ statusLabel(run.status) }}</span>
              <span class="spMuted">{{ formatTime(run.started_at || run.created_at) }}</span>
            </div>
            <p v-if="run.status_summary" class="spRunSummary">{{ run.status_summary }}</p>
          </li>
        </ul>
        <p v-else class="spMuted">No runs yet.</p>
      </section>

      <section v-if="details.changes?.length" class="spCard" data-test="changes">
        <h3 class="spTitle">Changes</h3>
        <ul class="spRuns">
          <li v-for="change in details.changes" :key="change.id" class="spRun">
            <div class="spRunHead">
              <span class="spChangeAuthor">{{ changeAuthor(change) }}</span>
              <span class="spMuted">{{ formatTime(change.created_at) }}</span>
            </div>
            <p class="spRunSummary">{{ changeLabel(change) }}</p>
          </li>
        </ul>
      </section>

      <section v-if="latestRun && running" class="spCard" data-test="run-log">
        <div class="spCardHead">
          <h3 class="spTitle">Running now</h3>
          <span class="spStatus" :class="`spStatus--${latestRun.status}`">{{ statusLabel(latestRun.status) }}</span>
        </div>
        <ol v-if="progress.length" ref="log" class="spProgress" @scroll="onLogScroll">
          <li v-for="(line, i) in progress" :key="i">{{ line }}</li>
        </ol>
        <p v-else class="spMuted">{{ latestRun.status === 'queued' ? 'Waiting to start…' : 'Starting…' }}</p>
      </section>
    </template>
    <p v-else-if="!error" class="spMuted">Loading play…</p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import StrategyBriefInput from './StrategyBriefInput.vue'
import { changeAuthor, changeLabel, useStrategyPlay } from '../../composables/useStrategyPlay'
import { monthDayTime } from '../../../r3/composables/usePlayTime'
import { RUN_STATUS_LABELS } from '../../../r3/composables/useStrategyRunLog'

const props = defineProps<{ playId: string }>()

const { details, error, saving, latestRun, running, progress, save, saveAndRun, runNow } = useStrategyPlay(() => props.playId)
const statusLabel = (status: string) => RUN_STATUS_LABELS[status] ?? status
const formatTime = (at: string | null) => monthDayTime(at, '')

const brief = ref('')
watch(() => details.value?.brief, (saved) => { brief.value = saved ?? '' }, { immediate: true })

const briefChanged = computed(() => brief.value.trim() !== (details.value?.brief ?? '').trim())
const canRun = computed(() => details.value?.status === 'active')
const canSave = computed(() => canRun.value && !!brief.value.trim() && briefChanged.value)

// The log follows new lines unless the reader scrolled up to read earlier ones.
const log = ref<HTMLElement | null>(null)
const following = ref(true)
function onLogScroll() {
  const el = log.value
  if (el) following.value = el.scrollHeight - el.scrollTop - el.clientHeight < 24
}
watch(() => progress.value.length, async () => {
  if (!following.value) return
  await nextTick()
  if (log.value) log.value.scrollTop = log.value.scrollHeight
}, { immediate: true })

</script>

<style scoped>
.strategyPlay {
  max-width: 720px;
  margin: 0 auto;
  padding: var(--space-6) 0 var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.spCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface, #fff);
}
.spCardHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.spTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
}
.spWhy,
.spAttest { display: grid; grid-template-columns: max-content 1fr; gap: 6px 16px; margin: 12px 0 0; font-family: var(--font-sans); font-size: var(--fs-sm); }
.spAttest dt { color: var(--color-text-secondary); }
.spAttest dd { margin: 0; color: var(--color-text-primary); }
.spAttest ul { margin: 0; padding-left: 16px; }
.spSummary,
.spNotes,
.spRunSummary {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  white-space: pre-wrap;
}
.spChangeAuthor {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.spMuted {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
.spError {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}
.spActions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--space-3);
}
.spActionsNote {
  flex-basis: 100%;
  text-align: right;
}
.spProgress {
  margin: 0;
  max-height: 360px;
  overflow-y: auto;
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-6);
  border-radius: var(--radius-md);
  background: var(--color-bg-sunk);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
.spProgress li:last-child {
  color: var(--color-text-primary);
}
.spStatus {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
}
.spStatus--running,
.spStatus--queued {
  color: var(--color-accent);
}
.spStatus--failed {
  color: var(--color-danger);
}
.spCounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-3);
}
.spCount {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface, #fff);
  color: inherit;
  text-decoration: none;
}
.spCount:hover {
  border-color: var(--color-accent);
}
.spCountNum {
  font-family: var(--font-display);
  font-size: var(--fs-xl);
}
.spCountLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
}
.spRuns {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.spRun {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.spRunHead {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
}
</style>
